import {
  CLOSE_AUTH,
  CLOSE_TAKEN,
  CLOSE_UNKNOWN_CALL,
  parseServerFrame,
  type CallEndReason,
  type CallState,
  type ClientFrame,
  type ServerFrame,
  type SignalKind,
} from "./protocol";

/**
 * One member's side of a doorbell call (doorbell live calls spec, "Call flow"
 * and "Signalling"): the call socket, the microphone/camera and the WebRTC
 * peer connection. Framework-free like `portal-socket-engine.ts`, so it
 * unit-tests in node with fakes; `useDoorbellCall` wires it to React and the
 * browser APIs.
 *
 * - **Media first.** `accept` gets the microphone (and camera) BEFORE it sends
 *   `accept`: a refused permission never wins the call and leaves the visitor
 *   talking to nobody. The page offers the other answer instead.
 * - **Won or not.** `state {connected, you: true}` is this socket's win;
 *   `you: false` means someone else answered (the 4409 close follows).
 * - **The member always offers**: the first offer on its winning `state`,
 *   every renegotiation (camera on/off), and an ICE restart whenever
 *   `state {connected}` arrives again (either side rejoined) or ICE fails. After
 *   a page reload the new peer connection's first offer carries `fresh: true`,
 *   so the tablet recreates its connection. One offer is in flight at a time;
 *   the tablet only answers.
 * - **Rejoin, never re-accept.** A socket that drops after `accept` is
 *   re-opened with `rejoin` until the 15 s grace has passed. After `ended` or
 *   any close code ≥ 4000 (4401 auth, 4403 not allowed, 4404 unknown call,
 *   4409 lost the accept / call over), nothing reconnects.
 * - **Heartbeat.** The server pings every 20 s; every `ping` gets a `pong`.
 */

export type CallPhase =
  | "idle"
  | "ringing"
  | "acquiring"
  | "accepting"
  | "connected"
  | "reconnecting"
  | "ended"
  | "taken"
  /** A newer window of this member took the call over (bare 4409, no `ended`). */
  | "replaced"
  | "failed";

export interface CallSnapshot {
  phase: CallPhase;
  answeredByName: string | null;
  answeredWithVideo: boolean | null;
  /** The last `state` the server sent. */
  serverState: CallState | null;
  endReason: CallEndReason | null;
  error: string | null;
  muted: boolean;
  /** This member's camera is on. */
  camera: boolean;
  remoteMuted: boolean;
  remoteCamera: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** ICE connected: media is flowing. */
  mediaConnected: boolean;
}

export const INITIAL_SNAPSHOT: CallSnapshot = {
  phase: "idle",
  answeredByName: null,
  answeredWithVideo: null,
  serverState: null,
  endReason: null,
  error: null,
  muted: false,
  camera: false,
  remoteMuted: false,
  remoteCamera: true,
  localStream: null,
  remoteStream: null,
  mediaConnected: false,
};

/** A party that is gone longer than this is gone (spec: 15 s grace). */
export const GRACE_MS = 15_000;
/** After an ICE restart offer, ICE must be connected within this long or the call is hung up. */
export const ICE_RESTART_MS = 10_000;
export const RECONNECT_DELAYS_MS: readonly number[] = [500, 1_000, 2_000, 4_000];
/** While only watching a ringing call, look again this long after a drop. */
export const WATCH_RETRY_MS = 2_000;

const OPEN = 1;
const FINAL: readonly CallPhase[] = ["ended", "taken", "replaced", "failed"];
const ON_CALL: readonly CallPhase[] = ["accepting", "connected", "reconnecting"];

/** The subset of the WebSocket API the session drives. */
export interface CallSocket {
  readyState: number;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: (() => void) | null;
  send(data: string): void;
  close(code?: number): void;
}

export interface TransceiverLike {
  direction: RTCRtpTransceiverDirection;
  sender: { replaceTrack(track: MediaStreamTrack | null): Promise<void>; setStreams?(...streams: MediaStream[]): void };
}

/** The subset of `RTCPeerConnection` the session drives. */
export interface PeerLike {
  iceConnectionState: string;
  remoteDescription: unknown;
  onicecandidate: ((ev: { candidate: { toJSON(): unknown } | null }) => void) | null;
  ontrack: ((ev: { streams: readonly MediaStream[]; track: MediaStreamTrack }) => void) | null;
  oniceconnectionstatechange: (() => void) | null;
  addTransceiver(trackOrKind: MediaStreamTrack | "audio" | "video", init?: RTCRtpTransceiverInit): TransceiverLike;
  createOffer(options?: RTCOfferOptions): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(description: RTCSessionDescriptionInit): Promise<void>;
  setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void>;
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void>;
  close(): void;
}

export interface CallSessionDeps {
  /** The guide's "Ring me" call: no remote party, no peer connection. */
  isTest: boolean;
  /** The call socket's URL with a fresh access token (refreshed first when it
   *  may have expired), or null when signed out. Asked for before every (re)connect. */
  socketUrl: () => string | null | Promise<string | null>;
  createSocket: (url: string) => CallSocket;
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createPeerConnection: (config: RTCConfiguration) => PeerLike;
  fetchIceServers: () => Promise<RTCIceServer[]>;
  makeStream: (tracks: MediaStreamTrack[]) => MediaStream;
  onChange: (snapshot: CallSnapshot) => void;
  onError?: (err: unknown) => void;
  graceMs?: number;
  now?: () => number;
}

export interface CallSession {
  /** Open the socket for a ringing call (watch it; `accept` answers). */
  join(): void;
  /** After a reload during the call: microphone, then `rejoin`. */
  rejoin(): Promise<void>;
  accept(video: boolean): Promise<void>;
  setMuted(muted: boolean): void;
  setCamera(on: boolean): Promise<void>;
  /** Send `hangup` and end — only while on the call. */
  hangup(): void;
  /** Stop everything, sending nothing. */
  dispose(): void;
}

/** What to tell the member when the browser refuses the microphone or camera. */
export function mediaErrorText(err: unknown, video: boolean): string {
  const name = (err as { name?: unknown } | null)?.name;
  if (name === "NotAllowedError" || name === "SecurityError") {
    return video
      ? "Allow the camera and microphone for this site to answer with video."
      : "Allow the microphone for this site to answer.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return video ? "No camera or microphone was found on this device." : "No microphone was found on this device.";
  }
  return video ? "Couldn't start your camera or microphone." : "Couldn't start your microphone.";
}

export function startCallSession(deps: CallSessionDeps): CallSession {
  const now = deps.now ?? Date.now;
  const graceMs = deps.graceMs ?? GRACE_MS;

  let snap: CallSnapshot = { ...INITIAL_SNAPSHOT };
  let socket: CallSocket | null = null;
  let disposed = false;
  /** This page sent `accept` or `rejoin` (a party), or only watches the call ring. */
  let party = false;
  /** `accept {video}` still to send when the socket opens. */
  let acceptOnOpen: boolean | null = null;
  let lostAt: number | null = null;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let pc: PeerLike | null = null;
  let videoTx: TransceiverLike | null = null;
  let localStream: MediaStream | null = null;
  /** Offers sent whose answer hasn't come back (a stale answer is ignored). */
  let outstanding = 0;
  let offerQueued = false;
  /** `startPeer` is fetching ICE servers: a second `you:true` must not start another peer. */
  let starting = false;
  /** This session saw `state {you: true}`: it was the answering party at some point. */
  let joined = false;
  /** The socket that already carried this party's `accept` / `rejoin`. */
  let acceptSentOn: CallSocket | null = null;
  let rejoinSentOn: CallSocket | null = null;
  /** A connect is in flight (token refresh): don't start another. */
  let connecting = false;
  /** ICE failed while the socket was down: the rejoin's restart must try to recover. */
  let iceFailedPending = false;
  /** Hangs up if ICE is still not connected this long after a restart offer. */
  let iceTimer: ReturnType<typeof setTimeout> | null = null;
  let remoteCandidates: RTCIceCandidateInit[] = [];
  /** The next offer starts a new connection after a reload: mark it `fresh`. */
  let freshOffer = false;

  const set = (patch: Partial<CallSnapshot>) => {
    snap = { ...snap, ...patch };
    deps.onChange(snap);
  };
  const isFinal = () => FINAL.includes(snap.phase);
  const report = (err: unknown) => deps.onError?.(err);
  const send = (frame: ClientFrame) => {
    if (socket && socket.readyState === OPEN) socket.send(JSON.stringify(frame));
  };
  const sendMedia = () => send({ type: "media", muted: snap.muted, camera: snap.camera });

  const clearRetry = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };
  const dropSocket = () => {
    const ws = socket;
    socket = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close(1000);
    } catch {
      // Already closed.
    }
  };
  const stopMedia = () => {
    localStream?.getTracks().forEach((t) => t.stop());
    localStream = null;
  };
  const clearIceTimer = () => {
    if (iceTimer !== null) {
      clearTimeout(iceTimer);
      iceTimer = null;
    }
  };
  const closePeer = () => {
    clearIceTimer();
    iceFailedPending = false;
    pc?.close();
    pc = null;
    videoTx = null;
    outstanding = 0;
    offerQueued = false;
    starting = false;
    remoteCandidates = [];
  };
  const finish = (phase: "ended" | "taken" | "replaced" | "failed", patch: Partial<CallSnapshot> = {}) => {
    clearRetry();
    dropSocket();
    closePeer();
    stopMedia();
    set({ ...patch, phase, localStream: null, remoteStream: null, mediaConnected: false });
  };

  const connect = () => {
    if (disposed || isFinal() || connecting) return;
    clearRetry();
    const result = deps.socketUrl();
    if (result !== null && typeof result === "object" && typeof (result as Promise<string | null>).then === "function") {
      connecting = true;
      (result as Promise<string | null>).then(
        (url) => {
          connecting = false;
          open(url);
        },
        (err) => {
          connecting = false;
          report(err);
          open(null);
        },
      );
    } else {
      open(result as string | null);
    }
  };

  const open = (url: string | null) => {
    if (disposed || isFinal() || socket) return;
    if (!url) {
      finish("failed", { error: "You are signed out. Sign in and open the call again." });
      return;
    }
    const ws = deps.createSocket(url);
    socket = ws;
    ws.onopen = () => {
      if (acceptOnOpen !== null) {
        const video = acceptOnOpen;
        acceptOnOpen = null;
        acceptSentOn = ws;
        send({ type: "accept", video });
      } else if (party) {
        rejoinSentOn = ws;
        send({ type: "rejoin" });
      }
    };
    ws.onmessage = (ev) => {
      if (socket !== ws) return;
      const frame = parseServerFrame(ev.data);
      if (frame) onFrame(frame).catch(report);
    };
    ws.onerror = () => {
      // onclose follows and decides.
    };
    ws.onclose = (ev) => {
      if (socket !== ws) return;
      socket = null;
      onClose(ev.code);
    };
  };

  const onClose = (code: number) => {
    if (disposed || isFinal()) return;
    if (code === CLOSE_TAKEN) {
      // A lost accept or an ended call has already said so (`state you:false`,
      // `ended`) and finished us. A bare 4409 while the call is live means a
      // newer window of this member replaced this socket: final, no reconnect.
      // "Taken" is a `state` naming someone else, then the server's close; a
      // member who was the answering party and sees a bare close was replaced.
      const live = snap.serverState === null || snap.serverState === "ringing" || (snap.serverState === "connected" && joined);
      finish(live ? "replaced" : "taken");
      return;
    }
    if (code === CLOSE_AUTH) {
      finish("failed", { error: "You are signed out. Sign in and open the call again." });
      return;
    }
    if (code === CLOSE_UNKNOWN_CALL) {
      finish("failed", { error: "This call could not be found." });
      return;
    }
    if (code >= 4000) {
      finish("failed", { error: "You can't join this call." });
      return;
    }
    if (snap.serverState !== null && snap.serverState !== "ringing" && snap.serverState !== "connected") {
      finish("ended");
      return;
    }
    if (!party) {
      retryTimer = setTimeout(connect, WATCH_RETRY_MS);
      return;
    }
    if (lostAt === null) lostAt = now();
    if (now() - lostAt >= graceMs) {
      finish("failed", { error: "The call was lost." });
      return;
    }
    if (snap.phase === "connected") set({ phase: "reconnecting" });
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
    attempt += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (lostAt !== null && now() - lostAt >= graceMs) {
        finish("failed", { error: "The call was lost." });
        return;
      }
      connect();
    }, delay);
  };

  /** Send an offer. One at a time: a second waits for the answer — except an
   *  ICE restart, which goes at once (the last answer may be lost with the socket). */
  const negotiate = async (iceRestart: boolean) => {
    const peer = pc;
    if (!peer) return;
    if (outstanding > 0 && !iceRestart) {
      offerQueued = true;
      return;
    }
    outstanding += 1;
    const offer = await peer.createOffer(iceRestart ? { iceRestart: true } : undefined);
    if (pc !== peer) return;
    await peer.setLocalDescription(offer);
    const fresh = freshOffer;
    freshOffer = false;
    send({
      type: "signal",
      kind: "offer",
      payload: { type: offer.type, sdp: offer.sdp },
      ...(fresh ? { fresh: true } : {}),
    });
  };

  const armIceTimer = () => {
    clearIceTimer();
    const peer = pc;
    iceTimer = setTimeout(() => {
      iceTimer = null;
      if (isFinal() || !peer || pc !== peer) return;
      if (peer.iceConnectionState === "connected" || peer.iceConnectionState === "completed") return;
      send({ type: "hangup" });
      finish("failed", { error: "Call failed" });
    }, ICE_RESTART_MS);
  };

  const startPeer = async () => {
    starting = true;
    let iceServers: RTCIceServer[];
    try {
      iceServers = await deps.fetchIceServers();
    } finally {
      starting = false;
    }
    const stream = localStream;
    if (disposed || isFinal() || !stream || pc) return;
    const peer = deps.createPeerConnection({ iceServers });
    pc = peer;
    peer.onicecandidate = (ev) => {
      if (ev.candidate) send({ type: "signal", kind: "candidate", payload: ev.candidate.toJSON() });
    };
    peer.ontrack = (ev) => set({ remoteStream: ev.streams[0] ?? deps.makeStream([ev.track]) });
    peer.oniceconnectionstatechange = () => {
      const state = peer.iceConnectionState;
      set({ mediaConnected: state === "connected" || state === "completed" });
      if (state === "connected" || state === "completed") {
        clearIceTimer();
        iceFailedPending = false;
      }
      // `disconnected` is transient. On `failed` with the socket open, restart
      // ICE once at once; with the socket down, remember it: the rejoin's
      // restart is the recovery. Either way the call is hung up if ICE is
      // still not up ICE_RESTART_MS after the restart offer.
      if (state === "failed" && !isFinal() && pc === peer && iceTimer === null) {
        if (socket && socket.readyState === OPEN) {
          negotiate(true).then(armIceTimer, report);
        } else {
          iceFailedPending = true;
        }
      }
    };
    const [audio] = stream.getAudioTracks();
    if (audio) peer.addTransceiver(audio, { direction: "sendrecv", streams: [stream] });
    const [video] = stream.getVideoTracks();
    // The visitor's video is always shown: a voice-only answer still receives it.
    videoTx = video
      ? peer.addTransceiver(video, { direction: "sendrecv", streams: [stream] })
      : peer.addTransceiver("video", { direction: "recvonly" });
    await negotiate(false);
  };

  const onState = async (
    state: CallState,
    answeredByName: string | null,
    answeredWithVideo: boolean | null,
    you: boolean,
  ) => {
    set({ serverState: state, answeredByName, answeredWithVideo });
    if (state === "ringing") {
      if (snap.phase === "idle") set({ phase: "ringing" });
      return;
    }
    // Over: the `ended` frame or the close says how.
    if (state !== "connected") return;
    // A watcher that never answered: someone else did.
    if (!party) {
      finish("taken");
      return;
    }
    // `you:false` is what every member socket gets on connect, before its
    // `rejoin` is handled; it is never final for the answering party. Rejoin
    // (once per socket) and wait: `you:true` means we're back, the server's
    // 4409 after this state means someone else answered.
    if (!you) {
      if (socket && rejoinSentOn !== socket && acceptSentOn !== socket) {
        rejoinSentOn = socket;
        send({ type: "rejoin" });
      }
      return;
    }
    joined = true;
    lostAt = null;
    attempt = 0;
    set({ phase: "connected" });
    if (deps.isTest) return;
    sendMedia();
    if (pc) {
      outstanding = 0; // offers in flight died with the old socket
      const recovering = iceFailedPending || pc.iceConnectionState === "failed";
      await negotiate(true);
      iceFailedPending = false;
      if (recovering) armIceTimer();
      return;
    }
    if (starting) return;
    try {
      await startPeer();
    } catch (err) {
      report(err);
      finish("failed", { error: "Couldn't start the call's audio and video." });
    }
  };

  const onSignal = async (kind: SignalKind, payload: unknown) => {
    const peer = pc;
    if (!peer) return;
    if (kind === "answer") {
      if (outstanding === 0) return; // nothing was asked for
      outstanding -= 1;
      if (outstanding > 0) return; // the answer to an offer we already replaced
      await peer.setRemoteDescription(payload as RTCSessionDescriptionInit);
      const queued = remoteCandidates;
      remoteCandidates = [];
      for (const candidate of queued) await peer.addIceCandidate(candidate);
      if (offerQueued) {
        offerQueued = false;
        await negotiate(false);
      }
      return;
    }
    if (kind === "candidate") {
      if (!peer.remoteDescription) {
        remoteCandidates.push(payload as RTCIceCandidateInit);
        return;
      }
      await peer.addIceCandidate(payload as RTCIceCandidateInit);
    }
    // No "offer" ever reaches the member: the member always offers.
  };

  const onFrame = async (frame: ServerFrame): Promise<void> => {
    switch (frame.type) {
      case "ping":
        send({ type: "pong" });
        return;
      case "state":
        await onState(frame.state, frame.answered_by_name, frame.answered_with_video, frame.you);
        return;
      case "signal":
        await onSignal(frame.kind, frame.payload);
        return;
      case "media":
        set({ remoteMuted: frame.muted, remoteCamera: frame.camera });
        return;
      case "ended":
        finish("ended", { endReason: frame.end_reason, serverState: "ended" });
        return;
      case "door":
        return; // The tablet's banner; nothing for the member.
    }
  };

  return {
    join() {
      if (snap.phase !== "idle" || socket) return;
      set({ phase: "ringing" });
      connect();
    },

    async rejoin() {
      if (snap.phase !== "idle") return;
      set({ phase: "acquiring", error: null });
      let stream: MediaStream;
      try {
        stream = await deps.getUserMedia({ audio: true, video: false });
      } catch (err) {
        finish("failed", { error: mediaErrorText(err, false) });
        return;
      }
      if (disposed) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      localStream = stream;
      party = true;
      freshOffer = true;
      set({ phase: "reconnecting", localStream: stream, camera: false, muted: false });
      connect();
    },

    async accept(video: boolean) {
      if (snap.phase !== "idle" && snap.phase !== "ringing") return;
      set({ phase: "acquiring", error: null });
      let stream: MediaStream;
      try {
        stream = await deps.getUserMedia({ audio: true, video: video ? { facingMode: "user" } : false });
      } catch (err) {
        if (!isFinal()) set({ phase: "ringing", error: mediaErrorText(err, video) });
        return;
      }
      if (disposed || isFinal()) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      localStream = stream;
      party = true;
      set({ phase: "accepting", localStream: stream, camera: stream.getVideoTracks().length > 0, muted: false });
      if (socket && socket.readyState === OPEN) {
        acceptSentOn = socket;
        send({ type: "accept", video });
      } else {
        acceptOnOpen = video;
        if (!socket) connect();
      }
    },

    setMuted(muted: boolean) {
      if (!localStream) return;
      for (const track of localStream.getAudioTracks()) track.enabled = !muted;
      set({ muted });
      sendMedia();
    },

    async setCamera(on: boolean) {
      const tx = videoTx;
      const stream = localStream;
      if (deps.isTest || snap.phase !== "connected" || !pc || !tx || !stream || on === snap.camera) return;
      if (on) {
        let track: MediaStreamTrack | undefined;
        try {
          [track] = (await deps.getUserMedia({ audio: false, video: { facingMode: "user" } })).getVideoTracks();
        } catch (err) {
          set({ error: mediaErrorText(err, true) });
          return;
        }
        if (!track || videoTx !== tx) {
          track?.stop();
          return;
        }
        stream.addTrack(track);
        await tx.sender.replaceTrack(track);
        tx.sender.setStreams?.(stream);
        tx.direction = "sendrecv";
      } else {
        await tx.sender.replaceTrack(null);
        tx.direction = "recvonly";
        for (const track of stream.getVideoTracks()) {
          stream.removeTrack(track);
          track.stop();
        }
      }
      set({ camera: on, error: null });
      sendMedia();
      await negotiate(false);
    },

    hangup() {
      if (!ON_CALL.includes(snap.phase)) return;
      send({ type: "hangup" });
      finish("ended", { endReason: "hangup_member" });
    },

    dispose() {
      disposed = true;
      clearRetry();
      dropSocket();
      closePeer();
      stopMedia();
    },
  };
}
